import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeFormPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    address: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    gp_name: '',
    gp_address: '',
    current_medications: '',
    mental_health_history: '',
    reason_for_seeking_therapy: '',
    previous_therapy: '',
    consent_data_processing: false,
    consent_terms: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Signature canvas handling
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#2A5A52';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getSignatureData = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consent_data_processing || !formData.consent_terms) {
      toast.error('Please accept both consent checkboxes');
      return;
    }

    if (!hasSignature) {
      toast.error('Please provide your signature');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/intake-form`,
        {
          ...formData,
          signature: getSignatureData()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Intake form submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="intake-form-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <h1 className="font-heading text-4xl font-light mb-2">Client Intake Form</h1>
            <p className="text-muted-foreground">
              Please complete this form before your first session. All information is confidential.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">Personal Information</h2>
              
              <div className="grid gap-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="mt-1 rounded-xl"
                      required
                      data-testid="intake-full-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      name="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="mt-1 rounded-xl"
                      required
                      data-testid="intake-dob"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    rows={2}
                    required
                    data-testid="intake-address"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    required
                    data-testid="intake-phone"
                  />
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">Emergency Contact</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="emergency_contact_name">Contact Name *</Label>
                  <Input
                    id="emergency_contact_name"
                    name="emergency_contact_name"
                    value={formData.emergency_contact_name}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    required
                    data-testid="intake-emergency-name"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact_phone">Contact Phone *</Label>
                  <Input
                    id="emergency_contact_phone"
                    name="emergency_contact_phone"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    required
                    data-testid="intake-emergency-phone"
                  />
                </div>
              </div>
            </div>

            {/* GP Information */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">GP Information (Optional)</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gp_name">GP Name</Label>
                  <Input
                    id="gp_name"
                    name="gp_name"
                    value={formData.gp_name}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    data-testid="intake-gp-name"
                  />
                </div>
                <div>
                  <Label htmlFor="gp_address">GP Practice Address</Label>
                  <Input
                    id="gp_address"
                    name="gp_address"
                    value={formData.gp_address}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    data-testid="intake-gp-address"
                  />
                </div>
              </div>
            </div>

            {/* Medical History */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">Medical & Mental Health History</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="current_medications">Current Medications</Label>
                  <Textarea
                    id="current_medications"
                    name="current_medications"
                    value={formData.current_medications}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    rows={2}
                    placeholder="List any medications you are currently taking"
                    data-testid="intake-medications"
                  />
                </div>

                <div>
                  <Label htmlFor="mental_health_history">Mental Health History</Label>
                  <Textarea
                    id="mental_health_history"
                    name="mental_health_history"
                    value={formData.mental_health_history}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    rows={3}
                    placeholder="Please describe any previous mental health diagnoses or concerns"
                    data-testid="intake-mental-history"
                  />
                </div>

                <div>
                  <Label htmlFor="previous_therapy">Previous Therapy Experience</Label>
                  <Textarea
                    id="previous_therapy"
                    name="previous_therapy"
                    value={formData.previous_therapy}
                    onChange={handleChange}
                    className="mt-1 rounded-xl"
                    rows={2}
                    placeholder="Have you had therapy before? If so, please describe"
                    data-testid="intake-previous-therapy"
                  />
                </div>
              </div>
            </div>

            {/* Reason for Therapy */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">Reason for Seeking Therapy</h2>
              
              <div>
                <Label htmlFor="reason_for_seeking_therapy">
                  Please describe what brings you to therapy today *
                </Label>
                <Textarea
                  id="reason_for_seeking_therapy"
                  name="reason_for_seeking_therapy"
                  value={formData.reason_for_seeking_therapy}
                  onChange={handleChange}
                  className="mt-1 rounded-xl"
                  rows={4}
                  required
                  data-testid="intake-reason"
                />
              </div>
            </div>

            {/* Consent & Signature */}
            <div className="bg-card rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(42,90,82,0.08)]">
              <h2 className="font-heading text-2xl mb-6">Consent & Signature</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent_data_processing"
                    checked={formData.consent_data_processing}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, consent_data_processing: checked })
                    }
                    data-testid="consent-data-checkbox"
                  />
                  <label htmlFor="consent_data_processing" className="text-sm leading-relaxed">
                    I consent to the processing of my personal data for the purposes of my 
                    psychological treatment, in accordance with GDPR , and the practice's privacy policy as outlined within BACP Ethical Framework. *
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent_terms"
                    checked={formData.consent_terms}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, consent_terms: checked })
                    }
                    data-testid="consent-terms-checkbox"
                  />
                  <label htmlFor="consent_terms" className="text-sm leading-relaxed">
                    I have read and agree to the Terms of Business and understand the 24-hour 
                    cancellation policy. I understand that sessions cancelled with less than 
                    24 hours notice may be charged in full. *
                  </label>
                </div>

                {/* Signature Canvas */}
                <div>
                  <Label>Your Signature *</Label>
                  <div className="mt-2 border-2 border-dashed border-border rounded-xl p-4 bg-white">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={150}
                      className="w-full border border-border rounded-lg cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      data-testid="signature-canvas"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-muted-foreground">
                        Sign above using your mouse or touch
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearSignature}
                        data-testid="clear-signature-btn"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <Button
                type="submit"
                className="rounded-full px-8 py-6"
                disabled={loading}
                data-testid="submit-intake-btn"
              >
                {loading ? 'Submitting...' : 'Submit Intake Form'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default IntakeFormPage;
